import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Button, TextField, InputAdornment } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchBusinessProcesses } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

const PROCESS_TYPE_LABELS = {
  CoreProcess: "businessProcess.type.coreProcess",
  BusinessSupportProcess: "businessProcess.type.businessSupportProcess",
  EnterpriseSupportProcess: "businessProcess.type.enterpriseSupportProcess",
};

const PROCESS_CLASS_LABELS = {
  Operation: "businessProcess.class.operation",
  CustomerRelationshipManagement: "businessProcess.class.crm",
  ProductLifeCycleManagement: "businessProcess.class.plm",
  HumanResourceManagement: "businessProcess.class.hrm",
  InvestorRelationshipManagement: "businessProcess.class.irm",
  PartnerRelationshipManagement: "businessProcess.class.prm",
  DataManagement: "businessProcess.class.dm",
  ItServiceManagement: "businessProcess.class.itsm",
  AssetManagement: "businessProcess.class.am",
  Financing: "businessProcess.class.financing",
  GeneralManagement: "businessProcess.class.generalManagement",
};

export default function BusinessProcessListPage() {
  const { businessDomainId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await fetchBusinessProcesses(businessDomainId));
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRowClick = (params) => {
    navigate(`/design/${businessDomainId}/business-processes/${params.row.businessProcessId}`);
  };

  const filteredRows = searchText
    ? rows.filter((r) => r.businessProcessName?.toLowerCase().includes(searchText.toLowerCase()))
    : rows;

  const columns = [
    { field: "businessProcessName", headerName: t("businessProcess.name"), flex: 1, minWidth: 200 },
    {
      field: "businessProcessType", headerName: t("businessProcess.type"), width: 200,
      valueFormatter: (value) => {
        const key = PROCESS_TYPE_LABELS[value];
        return key ? intl.formatMessage({ id: key }) : (value || "");
      },
    },
    {
      field: "businessProcessClass", headerName: t("businessProcess.class"), width: 200,
      valueFormatter: (value) => {
        const key = PROCESS_CLASS_LABELS[value];
        return key ? intl.formatMessage({ id: key }) : (value || "");
      },
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("businessProcess.list.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <TextField size="small" placeholder={t("common.search")} value={searchText} onChange={(e) => setSearchText(e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`/design/${businessDomainId}/business-processes/create`)}>{t("common.add")}</Button>
        </Box>
      </Box>
      <DataGrid
        rows={filteredRows}
        columns={columns}
        getRowId={(r) => r.businessProcessId}
        loading={loading}
        autoHeight
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        onRowClick={handleRowClick}
        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
        disableRowSelectionOnClick
      />
    </Box>
  );
}
